import type { AdService } from '@/modules/adv/ad-service'
import { BannerState } from '@/modules/adv/banner'
import type { BannerSequenceConfig } from '@/modules/adv/banner-sequence-config'
import type { BannerEventType, OutOfPageAdType } from '@/modules/adv/index'
import type { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { AdProviderNames, type PlatformType } from '@/types'

const PREROLL_WAIT_MS = 3000

type EventPayload = {
    totalAds: number
    currentAdIndex: number
    secondsLeft?: number
    banner?: OutOfPageBanner
}

type BannerSequenceEventType = BannerEventType | 'banner_closed' | 'update'
type BannerEventListenerArgs = [
    type: BannerSequenceEventType,
    callback: (event: Event & { detail?: EventPayload }) => void,
]

export class OutOfPageBannerSequence extends EventTarget {
    private banners: OutOfPageBanner[] = []
    private currentIndex = 0

    constructor(
        private config: BannerSequenceConfig,
        private adService: AdService,
        private type: OutOfPageAdType,
        private platform: PlatformType,
    ) {
        super()
    }

    private dispatch(event: BannerSequenceEventType, payload?: { secondsLeft?: number }) {
        this.dispatchEvent(
            new CustomEvent<EventPayload>(event, {
                detail: {
                    totalAds: this.banners.length,
                    currentAdIndex: this.currentIndex,
                    banner: this.banners.at(this.currentIndex),
                    ...payload,
                },
            }),
        )
    }

    private async handleNextBanner(banner: OutOfPageBanner, type: OutOfPageAdType) {
        const bannerInfo = this.config[type]

        if (!bannerInfo) {
            return
        }

        if (
            bannerInfo.conditions.maxDurationSec &&
            (!banner.instreamDuration || banner.instreamDuration >= bannerInfo.conditions.maxDurationSec)
        ) {
            return
        }

        if (bannerInfo.conditions.providers && !bannerInfo.conditions.providers.includes(banner.provider)) {
            return
        }

        if (bannerInfo.conditions.platforms && !bannerInfo.conditions.platforms.includes(this.platform)) {
            return
        }

        try {
            const nextBanner = await this.loadBanner(bannerInfo.nextType, false)
            if (nextBanner.provider === AdProviderNames.GOOGLE_INSTREAM) {
                this.banners.push(nextBanner)
                this.dispatch('update')
            }
        } catch {}
    }

    private setHandlers(banner: OutOfPageBanner) {
        banner.addEventListener('closed', () => {
            if (this.type === 'rewarded' && this.currentIndex > 0 && this.currentIndex === this.banners.length - 1) {
                this.dispatch('rewarded')
            }

            this.dispatch('banner_closed')
            const nextBanner = this.banners[this.currentIndex + 1]
            if (nextBanner) {
                this.currentIndex += 1
                this.showBanner(nextBanner)
            } else {
                this.dispatch('closed')
            }
        })

        banner.addEventListener('rewarded', () => {
            if (this.banners.length === 1) {
                this.dispatch('rewarded')
            }
        })

        banner.addEventListener('rendered', () => {
            if (this.currentIndex === 0) {
                this.dispatch('rendered')
            }
        })

        banner.addEventListener('viewable', () => {
            this.dispatch('update')
            if (this.currentIndex === 0) {
                this.dispatch('viewable')
            }
        })

        banner.addEventListener('timer', (event) => {
            this.dispatch('timer', {
                secondsLeft: event.detail?.secondsLeft,
            })
        })

        banner.addEventListener('clicked', () => {
            this.dispatch('clicked')
        })
    }

    private async loadBanner(type: OutOfPageAdType, isFirst: boolean): Promise<OutOfPageBanner> {
        const timeout = type === 'interstitial_preroll' ? PREROLL_WAIT_MS : undefined
        const banner = await this.adService.requestOutOfPageAd(type, timeout)

        const { promise, resolve, reject } = Promise.withResolvers<OutOfPageBanner>()

        const onReady = () => {
            if (isFirst) {
                this.dispatch('ready')
            }

            this.handleNextBanner(banner, type)
            resolve(banner)
        }

        const onEmpty = () => {
            if (isFirst) {
                this.dispatch('empty')
                reject(new Error('No ads available'))
            }
        }

        banner.addEventListener('empty', onEmpty)

        if (banner.state === BannerState.ready) {
            onReady()
        } else if (banner.state === BannerState.empty) {
            onEmpty()
        } else {
            banner.addEventListener('ready', onReady)
        }

        return promise
    }

    private showBanner(banner: OutOfPageBanner) {
        this.setHandlers(banner)
        banner.show()
    }

    public async load() {
        const banner = await this.loadBanner(this.type, true)
        this.banners.push(banner)
    }

    public show() {
        this.showBanner(this.banners[0])
    }

    public override addEventListener(...args: BannerEventListenerArgs) {
        super.addEventListener(...args)
    }

    public override removeEventListener(...args: BannerEventListenerArgs) {
        super.removeEventListener(...args)
    }
}
